import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  ArrowLeft, Plus, Pin, Lock, Heart, MessageSquare, Eye, Clock,
  Trash2
} from 'lucide-react';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export default function ForumCategory() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin';

  const [threads, setThreads] = useState([]);
  const [category, setCategory] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showNewThread, setShowNewThread] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [posting, setPosting] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadThreads = async (p = 1) => {
    try {
      const [threadsRes, catsRes] = await Promise.all([
        axios.get(`${API}/api/forum/threads?category_id=${categoryId}&page=${p}`, { headers }),
        axios.get(`${API}/api/forum/categories`, { headers }),
      ]);
      setThreads(threadsRes.data.threads);
      setTotal(threadsRes.data.total);
      setPages(threadsRes.data.pages);
      setPage(p);
      const cat = catsRes.data.categories.find(c => c.id === categoryId);
      setCategory(cat);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadThreads(); }, [categoryId]);

  const createThread = async () => {
    if (!newTitle.trim() || !newContent.trim()) return;
    setPosting(true);
    try {
      await axios.post(`${API}/api/forum/threads`, {
        title: newTitle.trim(), content: newContent.trim(), category_id: categoryId
      }, { headers });
      setShowNewThread(false);
      setNewTitle('');
      setNewContent('');
      toast.success('Thread created');
      loadThreads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create thread');
    } finally {
      setPosting(false);
    }
  };

  const toggleLike = async (threadId) => {
    try {
      const res = await axios.post(`${API}/api/forum/threads/${threadId}/like`, {}, { headers });
      setThreads(prev => prev.map(t => t.id === threadId ? {
        ...t, liked: res.data.liked, like_count: t.like_count + (res.data.liked ? 1 : -1)
      } : t));
    } catch (err) {
      console.error(err);
    }
  };

  const togglePin = async (threadId) => {
    try {
      const res = await axios.post(`${API}/api/forum/threads/${threadId}/pin`, {}, { headers });
      setThreads(prev => prev.map(t => t.id === threadId ? { ...t, pinned: res.data.pinned } : t));
      toast.success(res.data.pinned ? 'Thread pinned' : 'Thread unpinned');
    } catch (err) {
      toast.error('Failed');
    }
  };

  const toggleLock = async (threadId) => {
    try {
      const res = await axios.post(`${API}/api/forum/threads/${threadId}/lock`, {}, { headers });
      setThreads(prev => prev.map(t => t.id === threadId ? { ...t, locked: res.data.locked } : t));
      toast.success(res.data.locked ? 'Thread locked' : 'Thread unlocked');
    } catch (err) {
      toast.error('Failed');
    }
  };

  const deleteThread = async (threadId) => {
    if (!window.confirm('Delete this thread and all replies?')) return;
    try {
      await axios.delete(`${API}/api/forum/threads/${threadId}`, { headers });
      setThreads(prev => prev.filter(t => t.id !== threadId));
      toast.success('Thread deleted');
    } catch (err) {
      toast.error('Failed');
    }
  };

  return (
    <Layout>
      <div className="space-y-5 max-w-5xl mx-auto" data-testid="forum-category-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <Button variant="ghost" size="sm" onClick={() => navigate('/forum')} data-testid="back-to-forum" className="shrink-0 h-8 w-8 p-0">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold truncate">{category?.name || 'Category'}</h1>
              <p className="text-xs sm:text-sm text-gray-500 truncate">{category?.description}</p>
            </div>
          </div>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 shrink-0" onClick={() => setShowNewThread(true)} data-testid="new-thread-btn">
            <Plus className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">New Thread</span><span className="sm:hidden">New</span>
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
          </div>
        ) : threads.length === 0 ? (
          <div className="text-center py-16">
            <MessageSquare className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No threads yet. Start the conversation!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {threads.map(thread => (
              <Card key={thread.id} className={`border ${thread.pinned ? 'border-amber-200 bg-amber-50/30' : 'border-gray-100'} hover:shadow-sm transition-shadow`} data-testid={`thread-${thread.id}`}>
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap mb-1">
                        {thread.pinned && <Pin className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
                        {thread.locked && <Lock className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
                        <Link to={`/forum/thread/${thread.id}`} className="font-semibold text-gray-900 hover:text-emerald-700 transition-colors text-sm sm:text-base break-words" data-testid={`thread-link-${thread.id}`}>
                          {thread.title}
                        </Link>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-500 line-clamp-1">{thread.content}</p>
                      <div className="flex items-center gap-2 sm:gap-3 mt-2 text-[11px] sm:text-xs text-gray-400 flex-wrap">
                        <span className="font-medium text-gray-600">{thread.author_name}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{timeAgo(thread.created_at)}</span>
                        <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />{thread.reply_count}</span>
                        <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{thread.view_count || 0}</span>
                        <button onClick={() => toggleLike(thread.id)} className={`flex items-center gap-1 transition-colors ${thread.liked ? 'text-red-500' : 'hover:text-red-400'}`} data-testid={`like-thread-${thread.id}`}>
                          <Heart className={`w-3 h-3 ${thread.liked ? 'fill-current' : ''}`} />{thread.like_count}
                        </button>
                        {thread.last_reply_by && (
                          <span className="text-gray-400 hidden sm:inline">Last reply by {thread.last_reply_by}</span>
                        )}
                      </div>
                    </div>
                    {isAdmin && (
                      <div className="flex items-center gap-0.5 shrink-0">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => togglePin(thread.id)} title="Pin/Unpin">
                          <Pin className={`w-3.5 h-3.5 ${thread.pinned ? 'text-amber-500' : 'text-gray-400'}`} />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => toggleLock(thread.id)} title="Lock/Unlock">
                          <Lock className={`w-3.5 h-3.5 ${thread.locked ? 'text-red-500' : 'text-gray-400'}`} />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => deleteThread(thread.id)} title="Delete">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex justify-center gap-1.5 sm:gap-2 pt-4 flex-wrap">
                {Array.from({ length: pages }, (_, i) => (
                  <Button key={i + 1} variant={page === i + 1 ? 'default' : 'outline'} size="sm" onClick={() => loadThreads(i + 1)} className="h-8 w-8 p-0 text-xs">
                    {i + 1}
                  </Button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* New Thread Dialog */}
        <Dialog open={showNewThread} onOpenChange={setShowNewThread}>
          <DialogContent className="max-w-lg w-[calc(100vw-2rem)] sm:w-full">
            <DialogHeader>
              <DialogTitle>New Thread</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Thread title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} data-testid="new-thread-title" />
              <Textarea placeholder="Write your post..." rows={5} value={newContent} onChange={(e) => setNewContent(e.target.value)} data-testid="new-thread-content" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewThread(false)}>Cancel</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={createThread} disabled={posting || !newTitle.trim() || !newContent.trim()} data-testid="submit-thread-btn">
                {posting ? 'Posting...' : 'Post Thread'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
